import React, { useEffect, useMemo } from 'react';
import { Col, Row } from 'tea-component';
import classnames from 'classnames';
import { sentimentIconMap } from '@op/constants';
import SvgIcon from '@home/components/SvgIcon';
import useLanguage from '@op/hooks/useLanguage';
import { useHistory } from 'umi';
import { goToHotEvent } from '@op/utils/url';
import { DBO_V } from '@op/utils/format';
import LoadingCommon from '@op/components/LoadingCommon';
import { useRequestStats } from '@op/pages/feeds/hooks/useFeedsRequest';
import type { ISummaryItem } from '../../hooks/useSummaryEvent';
import { getChartTimeParams } from '@op/utils/time';

interface ISentimentCardProps {
  bgColor: string;
  title: string;
  sentiment: number;
  items: ISummaryItem[];
  percent: string;
  onItemClick?: (item: ISummaryItem) => void;
  hasEventStats: boolean;
}

interface IEventCardListProps {
  hasEventStats: boolean;
  sentimentGroups: Record<string, ISummaryItem[]>;
  loading?: boolean;
  filter?: any;
  gameInfo?: any;
  channelType?: string;
  summaryData?: any;
}

const sentimentConfig: {
  key: string;
  bgColor: string;
  title: string;
  sentiment: number;
}[] =
  [{
    key: 'positive',
    bgColor: 'rgba(231, 249, 239, 1)',
    title: 'Positive',
    sentiment: 5,
  },
  {
    key: 'negative',
    bgColor: 'rgba(253, 237, 237, 1)',
    title: 'Negative',
    sentiment: 1,
  },
  {
    key: 'neutral',
    bgColor: 'rgba(126, 146, 161, 0.1)',
    title: 'Neutral',
    sentiment: 3,
  }];

const TOP_N = 5;

function SentimentCard({
  bgColor,
  title,
  sentiment,
  items,
  percent,
  onItemClick,
  hasEventStats
}: ISentimentCardProps) {
  const { formatMessage } = useLanguage();

  return (
    <div className="dbo-ai-summary-event-card">
      <div className="dbo-ai-summary-event-card__title">
        <span className="dbo-ai-summary-event-card__icon">
          <SvgIcon type={sentimentIconMap[sentiment]}></SvgIcon>
        </span>
        {formatMessage({ id: title })}
        {percent && (
          <span className="dbo-ai-summary-event-card__percent">
            {percent}
          </span>
        )}
      </div>
      <div
        className="dbo-ai-summary-event-card__content"
        style={{ backgroundColor: bgColor }}
      >
        <div className="dbo-ai-summary-event-card__list">
          {items.map((item, index) => (
            <div
              key={item.cluster_id || index}
              className={classnames('dbo-ai-summary-event-card__item', {
                'dbo-ai-summary-event-card__item--clickable': item.clickable,
              })}
              onClick={item.clickable ? () => onItemClick?.(item) : undefined}
            >
              {
                hasEventStats && (
                  <>
                    {index + 1}
                    {'. '}
                  </>
                )
              }
              <span className="dbo-ai-summary-event-card__item-text">
                {item.title}
                {item.count !== undefined && (
                  <span className="dbo-ai-summary-event-card__item-value">
                    ({DBO_V.reviews(item.count)})
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function EventCardList({
  hasEventStats,
  sentimentGroups,
  loading: reportLoading = false,
  filter,
  gameInfo,
  channelType = 'total',
  summaryData,
}: IEventCardListProps) {
  const history = useHistory();

  // Fetch stats from /opinion_pc/feeds/stats for total count & percentages
  const { feedsStats, feedsStatsLoading, requestState } = useRequestStats({
    defaultLoading: true,
  });

  const statsFilter = useMemo(() => {
    if (!filter) return null;

    if (summaryData?.data_date) {
      const { start_time, end_time } = getChartTimeParams(summaryData.data_date, filter?.date_type);
      return {
        ...filter,
        start_time,
        end_time,
        channel_type: channelType === 'total' ? undefined : channelType,
      };
    }

    return {
      ...filter,
      channel_type: channelType === 'total' ? undefined : channelType,
    };
  }, [filter, channelType, summaryData]);

  useEffect(() => {
    if (statsFilter) {
      requestState(statsFilter);
    }
  }, [statsFilter]);

  const {
    total,
    positivePercent,
    negativePercent,
    neutralPercent,
  } = useMemo(() => {
    const {
      total = 0,
      bad_total = 0,
      neutral_total = 0,
      good_total = 0,
    } = feedsStats || {};

    let positivePercent = 0;
    let negativePercent = 0;
    let neutralPercent = 0;
    if (total) {
      positivePercent = (good_total / total) * 100;
      negativePercent = (bad_total / total) * 100;
      neutralPercent = (neutral_total / total) * 100;
    }

    return {
      total,
      positivePercent,
      negativePercent,
      neutralPercent,
    };
  }, [feedsStats]);

  const percentMap: Record<string, number> = {
    positive: positivePercent,
    negative: negativePercent,
    neutral: neutralPercent,
  };

  // Slice top N items per sentiment
  const displayGroups = useMemo(() => {
    const result: Record<string, ISummaryItem[]> = {};
    (['positive', 'negative', 'neutral'] as const).forEach((key) => {
      const items = sentimentGroups[key] || [];
      // Event stats items: sort by count desc, then take top N
      if (hasEventStats) {
        result[key] = [...items]
          .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
          .slice(0, TOP_N);
      } else {
        result[key] = items.slice(0, TOP_N);
      }
    });
    return result;
  }, [sentimentGroups, hasEventStats]);

  const handleItemClick = (item: ISummaryItem) => {
    if (!item.clickable || !gameInfo) return;
    goToHotEvent({
      gameInfo,
      data: {
        ...(filter || {}),
      },
      history,
      urlData: {
        sentimentValue: item.sentiment_type,
        cluster_id: item.cluster_id,
      },
    });
  };

  const loading = reportLoading || feedsStatsLoading;

  if (loading) {
    return (
      <LoadingCommon
        status="loading"
        style={{ height: 200 }}
      />
    );
  }

  const visibleCards = sentimentConfig.filter(
    (config) => (displayGroups[config.key]?.length ?? 0) > 0,
  );

  const colSpan =
    visibleCards.length > 0 ? Math.floor(24 / visibleCards.length) : 8;

  return (
    <Row gap={16}>
      {visibleCards.map((config) => {
        const percent =
          total > 0
            ? DBO_V.percentageFormat(percentMap[config.key])
            : '';

        return (
          <Col span={colSpan} key={config.key}>
            <SentimentCard
              bgColor={config.bgColor}
              title={config.title}
              sentiment={config.sentiment}
              items={displayGroups[config.key]}
              percent={percent}
              onItemClick={handleItemClick}
              hasEventStats={hasEventStats}
            />
          </Col>
        );
      })}
    </Row>
  );
}
